/**
 * Artist details store – single source of truth for artist details.
 * - ensureFetched(): GET once per session (hasFetchedInSession); use when opening Contact Details tab.
 * - fetchArtistDetails(): always runs GET; use after mutations (verify, modify request, secondary save) to refresh UI.
 * - On API failure, the store is cleared and user is redirected to login.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AxiosError } from "axios";
import type { ArtistDetailsData } from "@/types/artistDetails";
import { ArtistService } from "@/services/artistService";
import { useAuthStore } from "@/stores/authStore";
import { STORAGE_KEYS } from "@/storage/storageKeys";

interface ArtistDetailsState {
  data: ArtistDetailsData | null;
  isLoading: boolean;
  error: string | null;
  /** In-memory only: GET runs once per session until clear or refetch. */
  hasFetchedInSession: boolean;

  setArtistDetails: (data: ArtistDetailsData | null) => void;
  fetchArtistDetails: () => Promise<ArtistDetailsData | null>;
  /** Fetches only if !hasFetchedInSession. Call when opening Contact Details (or other section that needs details). */
  ensureFetched: () => Promise<ArtistDetailsData | null>;
  clearArtistDetails: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

let inFlightPromise: Promise<ArtistDetailsData | null> | null = null;
/**
 * Used to prevent stale in-flight GET responses from overwriting a newer update
 * (e.g. Express Booking enable updates eb/eb_tnc while UserHome fetch is still in-flight).
 */
let updateSeq = 0;

export const useArtistDetailsStore = create<ArtistDetailsState>()(
  persist(
    (set, get) => ({
      data: null,
      isLoading: false,
      error: null,
      hasFetchedInSession: false,

      fetchArtistDetails: async () => {
        if (inFlightPromise) return inFlightPromise;
        inFlightPromise = (async () => {
          const requestSeq = ++updateSeq;
          set({ isLoading: true, error: null });
          try {
            const res = await ArtistService.getArtistDetails();
            if (res.success && res.data) {
              // If a newer update happened while this request was in-flight, ignore it.
              if (requestSeq !== updateSeq) {
                set({ isLoading: false });
                return res.data;
              }
              set({ data: res.data, error: null, isLoading: false, hasFetchedInSession: true });
              return res.data;
            }
            set({ isLoading: false });
            return null;
          } catch (err) {
            const axiosError = err as AxiosError | undefined;
            const status = axiosError?.response?.status;

            // Only treat explicit auth failures as "session expired".
            if (status === 401 || status === 403) {
              // Hard auth failure: clear all artist data once, mark this session as "fetched",
              // and let the login flow handle the redirect. This prevents repeated /details calls.
              get().clearArtistDetails();
              await useAuthStore.getState().logout();
              set({ isLoading: false, hasFetchedInSession: true });
              if (typeof window !== "undefined") {
                if (!sessionStorage.getItem(STORAGE_KEYS.LOGIN_REDIRECT_MESSAGE)) {
                  sessionStorage.setItem(
                    STORAGE_KEYS.LOGIN_REDIRECT_MESSAGE,
                    "Your session has expired. Please sign in again.",
                  );
                }
              }
            } else {
              // Network / server errors: don't force logout, just surface an error and
              // also mark as fetched to avoid hammering the endpoint until user retries.
              // If a newer update happened while this request was in-flight, ignore error UI.
              if (requestSeq === updateSeq) {
                set({
                  isLoading: false,
                  hasFetchedInSession: true,
                  error: "Failed to load artist details. Please try again.",
                });
              } else {
                set({ isLoading: false });
              }
            }
            return null;
          } finally {
            inFlightPromise = null;
          }
        })();
        return inFlightPromise;
      },

      ensureFetched: async () => {
        const { hasFetchedInSession, fetchArtistDetails, data } = get();
        if (hasFetchedInSession) return data;
        return fetchArtistDetails();
      },

      setArtistDetails: (data) => {
        updateSeq += 1;
        set({ data, error: null, hasFetchedInSession: Boolean(data), isLoading: false });
      },
      clearArtistDetails: () => {
        updateSeq += 1;
        set({ data: null, error: null, isLoading: false, hasFetchedInSession: false });
      },

      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
    }),
    { name: "artist-details-storage" }
  )
);
